import React from 'react';
export const DEFAULT_SIDER_WIDTH = 208;
export const MIN_SIDER_WIDTH = 180;
export const MAX_SIDER_WIDTH = 260;
export const SIDER_WIDTH_STORAGE_KEY = 'easy-record:sider-width:v2';
export const clampWidth = (value: number) =>
  Math.min(MAX_SIDER_WIDTH, Math.max(MIN_SIDER_WIDTH, Math.round(value)));
export function getStoredSiderWidth() {
  if (typeof window === 'undefined') return DEFAULT_SIDER_WIDTH;
  try {
    const stored = localStorage.getItem(SIDER_WIDTH_STORAGE_KEY);
    return stored !== null &&
      stored.trim() !== '' &&
      Number.isFinite(Number(stored))
      ? clampWidth(Number(stored))
      : DEFAULT_SIDER_WIDTH;
  } catch {
    return DEFAULT_SIDER_WIDTH;
  }
}
export default function SiderResizeHandle({
  enabled,
  siderWidth,
  onResizeWidth,
}: {
  enabled: boolean;
  siderWidth: number;
  onResizeWidth: (value: number) => void;
}) {
  const [geometry, setGeometry] = React.useState<{
    left: number;
    top: number;
  } | null>(null);
  const [dragging, setDragging] = React.useState(false);
  React.useEffect(() => {
    const measure = () => {
      const sider = document.querySelector('.ant-pro-sider');
      const header = document.querySelector('.ant-pro-layout-header');
      const rect = sider?.getBoundingClientRect();
      const top = header?.getBoundingClientRect().bottom || 44;
      const next =
        enabled && window.innerWidth > 768 && rect && rect.width > 90
          ? { left: Math.round(rect.right) - 6, top: Math.max(top + 56, 100) }
          : null;
      setGeometry((previous) =>
        previous?.left === next?.left && previous?.top === next?.top
          ? previous
          : next,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    let observed: Element | null = null;
    const attach = () => {
      const sider = document.querySelector('.ant-pro-sider');
      if (observed === sider) return;
      if (observed) observer.unobserve(observed);
      observed = sider;
      if (observed) observer.observe(observed);
      measure();
    };
    attach();
    const mutations = new MutationObserver(attach);
    mutations.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      mutations.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [enabled, siderWidth]);
  const apply = (width: number) => {
    const next = clampWidth(width);
    onResizeWidth(next);
    try {
      localStorage.setItem(SIDER_WIDTH_STORAGE_KEY, String(next));
    } catch {
      /* Width remains usable for this session. */
    }
  };
  if (!geometry) return null;
  return (
    // biome-ignore lint/a11y/useSemanticElements: Interactive window splitter requires ARIA range values, not a document separator.
    <div
      role="separator"
      aria-label="调整侧栏宽度"
      aria-orientation="vertical"
      aria-valuemin={MIN_SIDER_WIDTH}
      aria-valuemax={MAX_SIDER_WIDTH}
      aria-valuenow={siderWidth}
      tabIndex={0}
      className={`srbs-resize ${dragging ? 'srbs-resize-active' : ''}`}
      style={{ left: geometry.left, top: geometry.top }}
      onKeyDown={(event) => {
        if (['ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault();
          apply(
            siderWidth +
              (event.key === 'ArrowLeft' ? -1 : 1) * (event.shiftKey ? 24 : 8),
          );
        }
      }}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
      }}
      onPointerMove={(event) => {
        if (dragging) {
          const rect = document
            .querySelector('.ant-pro-sider')
            ?.getBoundingClientRect();
          if (rect) apply(event.clientX - rect.left);
        }
      }}
      onPointerUp={(event) => {
        if (dragging) {
          const rect = document
            .querySelector('.ant-pro-sider')
            ?.getBoundingClientRect();
          if (rect) apply(event.clientX - rect.left);
        }
        setDragging(false);
        if (event.currentTarget.hasPointerCapture(event.pointerId))
          event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onPointerCancel={() => setDragging(false)}
      onLostPointerCapture={() => setDragging(false)}
    />
  );
}
